import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  addBinGuidelineException,
  createBinGuideline,
  deleteBinGuideline,
  deleteBinGuidelineException,
  getOrgBinGuidelines,
  setBinGuidelineMaterials,
  updateBinGuideline,
} from 'services/adminGuidelinesService';
import { getMaterials, getOrganizations, searchItems } from 'services/lookupsService';
import {
  BinGuidelineDto,
  Item,
  ItemType,
  Material,
  OrganizationLookup,
  OrgBinGuidelinesDto,
} from 'types/api.types';
import useUser from 'hooks/useUser';
import { UserRole } from 'types/auth.types';

const EXCEPTIONS_INFO_COPY =
  'Exclude Exception: Removes a specific item from the bin, even if its material is accepted. ' +
  'Include Exception: Allows a specific item in the bin, even if its material is not accepted.';

function getCatalogItemLabel(item: Item, materialsById: Map<number, Material>): string {
  const materialName = materialsById.get(item.material)?.name?.trim();
  if (materialName) {
    return `${item.name} (${materialName})`;
  }
  if (item.type === ItemType.Reuse) {
    return `${item.name} (reuse)`;
  }
  return item.name;
}

type ToastSeverity = 'success' | 'error';

interface AddExceptionDraft {
  selectedItem: Item | null;
  ruleType: 'include' | 'exclude';
  isAdding: boolean;
}

const emptyAddExceptionDraft: AddExceptionDraft = {
  selectedItem: null,
  ruleType: 'include',
  isAdding: false,
};

const BinGuidelines: React.FC = () => {
  const { user, loading: userLoading } = useUser();
  const isOrgAdmin = user?.role === UserRole.OrgAdmin;
  const adminOrganizationIds = useMemo(
    () => user?.adminOrganizationIds ?? [],
    [user?.adminOrganizationIds],
  );
  const [organizations, setOrganizations] = useState<OrganizationLookup[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
  const [guidelines, setGuidelines] = useState<OrgBinGuidelinesDto | null>(null);
  const [topLevelMaterials, setTopLevelMaterials] = useState<Material[]>([]);
  const [catalogMaterials, setCatalogMaterials] = useState<Material[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isGuidelinesLoading, setIsGuidelinesLoading] = useState(false);
  const [isCreateBinDialogOpen, setCreateBinDialogOpen] = useState(false);
  const [newBinTitle, setNewBinTitle] = useState('');
  const [newBinColor, setNewBinColor] = useState('#1976d2');
  const [newBinContrastColor, setNewBinContrastColor] = useState('#ffffff');
  const [isCreatingBin, setIsCreatingBin] = useState(false);
  const [materialsSavingByBin, setMaterialsSavingByBin] = useState<Record<number, boolean>>({});
  const [materialDraftsByBin, setMaterialDraftsByBin] = useState<Record<number, number[]>>({});
  const [exceptionDraftsByBin, setExceptionDraftsByBin] = useState<Record<number, AddExceptionDraft>>(
    {},
  );
  const [exceptionSearchOptionsByBin, setExceptionSearchOptionsByBin] = useState<
    Record<number, Item[]>
  >({});
  const [exceptionSearchLoadingByBin, setExceptionSearchLoadingByBin] = useState<
    Record<number, boolean>
  >({});
  const [isDeletingExceptionById, setIsDeletingExceptionById] = useState<Record<number, boolean>>({});
  const [isDeletingBinById, setIsDeletingBinById] = useState<Record<number, boolean>>({});
  const [isUpdatingBinById, setIsUpdatingBinById] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState<{ message: string; severity: ToastSeverity } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const selectedOrg = useMemo(
    () => organizations.find((org) => org.id === selectedOrgId),
    [organizations, selectedOrgId],
  );

  const showErrorToast = (message: string) => setToast({ message, severity: 'error' });
  const showSuccessToast = (message: string) => setToast({ message, severity: 'success' });

  const loadGuidelines = async (orgId: number) => {
    setIsGuidelinesLoading(true);
    setErrorMessage(null);
    const result = await getOrgBinGuidelines(orgId);
    if (!result) {
      setGuidelines(null);
      setErrorMessage('Unable to load bin guidelines for this organization.');
      setIsGuidelinesLoading(false);
      return;
    }

    setGuidelines(result);
    setMaterialDraftsByBin(
      result.bins.reduce(
        (acc, bin) => ({
          ...acc,
          [bin.id]: bin.materialIds,
        }),
        {},
      ),
    );
    setExceptionDraftsByBin(
      result.bins.reduce(
        (acc, bin) => ({
          ...acc,
          [bin.id]: { ...emptyAddExceptionDraft },
        }),
        {},
      ),
    );
    setExceptionSearchOptionsByBin({});
    setIsGuidelinesLoading(false);
  };

  useEffect(() => {
    if (userLoading) {
      return;
    }

    const loadInitialData = async () => {
      setIsInitialLoading(true);
      const [orgs, materials] = await Promise.all([getOrganizations(), getMaterials()]);

      if (!orgs || !materials) {
        setErrorMessage('Unable to load organizations or materials.');
        setIsInitialLoading(false);
        return;
      }

      const allowedOrgs = isOrgAdmin
        ? orgs.filter((org) => adminOrganizationIds.includes(org.id))
        : orgs;

      if (isOrgAdmin && allowedOrgs.length === 0) {
        setErrorMessage('You do not have access to any organizations.');
        setIsInitialLoading(false);
        return;
      }

      const topLevel = materials.filter((material) => material.parentMaterialId == null);
      setOrganizations(allowedOrgs);
      setTopLevelMaterials(topLevel);
      setCatalogMaterials(materials);

      if (allowedOrgs.length > 0) {
        setSelectedOrgId(allowedOrgs[0].id);
      }
      setIsInitialLoading(false);
    };

    loadInitialData();
  }, [userLoading, isOrgAdmin, adminOrganizationIds]);

  useEffect(() => {
    if (typeof selectedOrgId === 'number') {
      loadGuidelines(selectedOrgId);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    return () => {
      Object.values(searchTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const updateBinInState = (updatedBin: BinGuidelineDto) => {
    setGuidelines((current) => {
      if (!current) return current;
      return {
        ...current,
        bins: current.bins.map((bin) => (bin.id === updatedBin.id ? updatedBin : bin)),
      };
    });
    setMaterialDraftsByBin((current) => ({
      ...current,
      [updatedBin.id]: updatedBin.materialIds,
    }));
  };

  const handleCreateBin = async () => {
    if (typeof selectedOrgId !== 'number') return;
    if (!newBinTitle.trim()) {
      showErrorToast('Bin title is required.');
      return;
    }

    setIsCreatingBin(true);
    const created = await createBinGuideline(selectedOrgId, {
      title: newBinTitle.trim(),
      color: newBinColor,
      contrastColor: newBinContrastColor,
    });
    setIsCreatingBin(false);

    if (!created) {
      showErrorToast('Unable to create bin.');
      return;
    }

    setGuidelines((current) =>
      current
        ? {
            ...current,
            bins: [...current.bins, created],
          }
        : current,
    );
    setMaterialDraftsByBin((current) => ({ ...current, [created.id]: created.materialIds }));
    setExceptionDraftsByBin((current) => ({ ...current, [created.id]: { ...emptyAddExceptionDraft } }));
    setCreateBinDialogOpen(false);
    setNewBinTitle('');
    setNewBinColor('#1976d2');
    setNewBinContrastColor('#ffffff');
    showSuccessToast('Bin created.');
  };

  const handleBinFieldSave = async (binId: number, payload: Partial<BinGuidelineDto>) => {
    if (typeof selectedOrgId !== 'number' || !guidelines) return;
    const existing = guidelines.bins.find((bin) => bin.id === binId);
    if (!existing) return;

    const nextTitle = payload.title ?? existing.title;
    if (!nextTitle.trim()) {
      showErrorToast('Bin title is required.');
      return;
    }

    setIsUpdatingBinById((current) => ({ ...current, [binId]: true }));
    const updated = await updateBinGuideline(selectedOrgId, binId, {
      title: nextTitle.trim(),
      color: payload.color ?? existing.color,
      contrastColor: payload.contrastColor ?? existing.contrastColor,
    });
    setIsUpdatingBinById((current) => ({ ...current, [binId]: false }));

    if (!updated) {
      showErrorToast('Unable to update bin details.');
      return;
    }
    updateBinInState(updated);
    showSuccessToast('Bin details updated.');
  };

  const handleMaterialToggle = (binId: number, materialId: number, checked: boolean) => {
    setMaterialDraftsByBin((current) => {
      const currentValues = current[binId] ?? [];
      const nextValues = checked
        ? currentValues.includes(materialId)
          ? currentValues
          : [...currentValues, materialId]
        : currentValues.filter((id) => id !== materialId);
      return { ...current, [binId]: nextValues };
    });
  };

  const handleSaveMaterials = async (binId: number) => {
    if (typeof selectedOrgId !== 'number') return;
    const materialIds = materialDraftsByBin[binId] ?? [];
    setMaterialsSavingByBin((current) => ({ ...current, [binId]: true }));
    const updated = await setBinGuidelineMaterials(selectedOrgId, binId, { materialIds });
    setMaterialsSavingByBin((current) => ({ ...current, [binId]: false }));

    if (!updated) {
      showErrorToast('Unable to save materials for this bin.');
      return;
    }

    updateBinInState(updated);
    showSuccessToast('Allowed materials saved.');
  };

  const materialsById = useMemo(
    () => new Map(catalogMaterials.map((material) => [material.id, material])),
    [catalogMaterials],
  );

  const subMaterialsByParentId = useMemo(() => {
    const map = new Map<number, Material[]>();
    catalogMaterials.forEach((material) => {
      if (material.parentMaterialId == null) {
        return;
      }
      const list = map.get(material.parentMaterialId) ?? [];
      list.push(material);
      map.set(material.parentMaterialId, list);
    });
    map.forEach((children, parentId) => {
      map.set(
        parentId,
        [...children].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
      );
    });
    return map;
  }, [catalogMaterials]);

  const getItemLabel = useCallback(
    (item: Item) => getCatalogItemLabel(item, materialsById),
    [materialsById],
  );

  const handleExceptionItemSearch = useCallback((binId: number, query: string) => {
    if (searchTimersRef.current[binId]) {
      clearTimeout(searchTimersRef.current[binId]);
    }

    const trimmed = query.trim();
    if (!trimmed) {
      setExceptionSearchOptionsByBin((current) => ({ ...current, [binId]: [] }));
      setExceptionSearchLoadingByBin((current) => ({ ...current, [binId]: false }));
      return;
    }

    setExceptionSearchLoadingByBin((current) => ({ ...current, [binId]: true }));
    searchTimersRef.current[binId] = setTimeout(async () => {
      const results = await searchItems(trimmed);
      setExceptionSearchOptionsByBin((current) => ({
        ...current,
        [binId]: results ?? [],
      }));
      setExceptionSearchLoadingByBin((current) => ({ ...current, [binId]: false }));
    }, 300);
  }, []);

  const handleAddException = async (binId: number) => {
    if (typeof selectedOrgId !== 'number') return;
    const draft = exceptionDraftsByBin[binId] ?? emptyAddExceptionDraft;
    if (!draft.selectedItem) {
      showErrorToast('Select an item before adding an exception.');
      return;
    }

    setExceptionDraftsByBin((current) => ({
      ...current,
      [binId]: { ...draft, isAdding: true },
    }));

    const updated = await addBinGuidelineException(selectedOrgId, binId, {
      itemId: draft.selectedItem.id,
      ruleType: draft.ruleType,
    });

    setExceptionDraftsByBin((current) => ({
      ...current,
      [binId]: { ...emptyAddExceptionDraft },
    }));
    setExceptionSearchOptionsByBin((current) => ({ ...current, [binId]: [] }));

    if (!updated) {
      showErrorToast('Unable to add exception.');
      return;
    }

    updateBinInState(updated);
    showSuccessToast('Exception saved.');
  };

  const handleDeleteBin = async (binId: number) => {
    if (typeof selectedOrgId !== 'number') return;
    if (!window.confirm('Delete this bin? Its materials and exception rules will be removed.')) {
      return;
    }

    setIsDeletingBinById((current) => ({ ...current, [binId]: true }));
    const deleted = await deleteBinGuideline(selectedOrgId, binId);
    setIsDeletingBinById((current) => ({ ...current, [binId]: false }));

    if (!deleted) {
      showErrorToast('Unable to delete bin.');
      return;
    }

    setGuidelines((current) => {
      if (!current) return current;
      return {
        ...current,
        bins: current.bins.filter((bin) => bin.id !== binId),
      };
    });
    showSuccessToast('Bin deleted.');
  };

  const handleDeleteException = async (binId: number, exceptionId: number) => {
    if (typeof selectedOrgId !== 'number') return;
    setIsDeletingExceptionById((current) => ({ ...current, [exceptionId]: true }));
    const deleted = await deleteBinGuidelineException(selectedOrgId, binId, exceptionId);
    setIsDeletingExceptionById((current) => ({ ...current, [exceptionId]: false }));

    if (!deleted) {
      showErrorToast('Unable to delete exception.');
      return;
    }

    setGuidelines((current) => {
      if (!current) return current;
      return {
        ...current,
        bins: current.bins.map((bin) =>
          bin.id === binId
            ? {
                ...bin,
                exceptions: bin.exceptions.filter((ex) => ex.id !== exceptionId),
              }
            : bin,
        ),
      };
    });
    showSuccessToast('Exception deleted.');
  };

  if (isInitialLoading || userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', p: { xs: 1, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Bin guidelines
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure which materials are accepted in each bin. Items are accepted when their material
            is allowed, unless overridden by an exception rule.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateBinDialogOpen(true)}>
          Add bin
        </Button>
      </Stack>

      {organizations.length > 1 ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              label="Organization"
              value={selectedOrgId}
              onChange={(event) => setSelectedOrgId(event.target.value as number)}
              displayEmpty
              renderValue={() => {
                if (selectedOrgId === '') return 'Select organization';
                const org = organizations.find((o) => o.id === selectedOrgId);
                return org?.name ?? 'Select organization';
              }}
            >
              {organizations.length === 0 ? (
                <MenuItem disabled value="">
                  No organizations available
                </MenuItem>
              ) : (
                organizations.map((organization) => (
                  <MenuItem key={organization.id} value={organization.id}>
                    {organization.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Paper>
      ) : selectedOrg ? (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Organization
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {selectedOrg.name}
          </Typography>
        </Paper>
      ) : null}

      {isGuidelinesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : !guidelines || guidelines.bins.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>No bins configured for {selectedOrg?.name ?? 'this organization'} yet.</Typography>
        </Paper>
      ) : (
        guidelines.bins.map((bin) => {
          const materialDraft = materialDraftsByBin[bin.id] ?? bin.materialIds;
          const exceptionDraft = exceptionDraftsByBin[bin.id] ?? emptyAddExceptionDraft;
          const isUpdatingBin = !!isUpdatingBinById[bin.id];
          const isDeletingBin = !!isDeletingBinById[bin.id];
          const exceptionSearchOptions = exceptionSearchOptionsByBin[bin.id] ?? [];
          const isExceptionSearchLoading = !!exceptionSearchLoadingByBin[bin.id];

          return (
            <Accordion key={bin.id} defaultExpanded sx={{ mb: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                  width="100%"
                  gap={1.5}
                >
                  <TextField
                    size="small"
                    label="Bin title"
                    value={bin.title}
                    onChange={(event) => {
                      const value = event.target.value;
                      setGuidelines((current) => {
                        if (!current) return current;
                        return {
                          ...current,
                          bins: current.bins.map((currentBin) =>
                            currentBin.id === bin.id ? { ...currentBin, title: value } : currentBin,
                          ),
                        };
                      });
                    }}
                    onBlur={() => handleBinFieldSave(bin.id, { title: bin.title })}
                    onClick={(event) => event.stopPropagation()}
                    onFocus={(event) => event.stopPropagation()}
                    sx={{ minWidth: 220 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      label="Color"
                      value={bin.color}
                      onChange={(event) => {
                        const value = event.target.value;
                        setGuidelines((current) => {
                          if (!current) return current;
                          return {
                            ...current,
                            bins: current.bins.map((currentBin) =>
                              currentBin.id === bin.id ? { ...currentBin, color: value } : currentBin,
                            ),
                          };
                        });
                      }}
                      onBlur={() => handleBinFieldSave(bin.id, { color: bin.color })}
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                      sx={{ width: 130 }}
                    />
                    <TextField
                      size="small"
                      label="Contrast"
                      value={bin.contrastColor}
                      onChange={(event) => {
                        const value = event.target.value;
                        setGuidelines((current) => {
                          if (!current) return current;
                          return {
                            ...current,
                            bins: current.bins.map((currentBin) =>
                              currentBin.id === bin.id
                                ? { ...currentBin, contrastColor: value }
                                : currentBin,
                            ),
                          };
                        });
                      }}
                      onBlur={() => handleBinFieldSave(bin.id, { contrastColor: bin.contrastColor })}
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.stopPropagation()}
                      sx={{ width: 130 }}
                    />
                    <Chip
                      label="Preview"
                      sx={{
                        color: bin.contrastColor,
                        backgroundColor: bin.color,
                        fontWeight: 700,
                      }}
                    />
                    {isUpdatingBin && <CircularProgress size={18} />}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteBin(bin.id);
                      }}
                      disabled={isDeletingBin}
                    >
                      {isDeletingBin ? 'Deleting...' : 'Delete'}
                    </Button>
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Allowed materials
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Select a material to allow all items of that type in this bin. For materials with
                  sub-types, you can allow the parent or specific sub-materials.
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {topLevelMaterials.map((parent) => {
                    const children = subMaterialsByParentId.get(parent.id) ?? [];
                    return (
                      <Box key={parent.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={materialDraft.includes(parent.id)}
                              onChange={(event) =>
                                handleMaterialToggle(bin.id, parent.id, event.target.checked)
                              }
                            />
                          }
                          label={parent.name}
                        />
                        {children.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              pl: 3.5,
                              mt: 0.25,
                            }}
                          >
                            {children.map((child) => (
                              <FormControlLabel
                                key={child.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={materialDraft.includes(child.id)}
                                    onChange={(event) =>
                                      handleMaterialToggle(bin.id, child.id, event.target.checked)
                                    }
                                  />
                                }
                                label={child.name}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleSaveMaterials(bin.id)}
                    disabled={materialsSavingByBin[bin.id]}
                  >
                    {materialsSavingByBin[bin.id] ? 'Saving...' : 'Save materials'}
                  </Button>
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Exceptions
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {EXCEPTIONS_INFO_COPY}
                </Alert>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bin.exceptions.map((exception) => (
                      <TableRow key={exception.id}>
                        <TableCell>{exception.itemName}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{exception.ruleType}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDeleteException(bin.id, exception.id)}
                            disabled={isDeletingExceptionById[exception.id]}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ py: 1, minWidth: 260 }}>
                        <Autocomplete
                          size="small"
                          options={exceptionSearchOptions}
                          loading={isExceptionSearchLoading}
                          value={exceptionDraft.selectedItem}
                          onChange={(_, item) =>
                            setExceptionDraftsByBin((current) => ({
                              ...current,
                              [bin.id]: {
                                ...(current[bin.id] ?? emptyAddExceptionDraft),
                                selectedItem: item,
                              },
                            }))
                          }
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input') {
                              handleExceptionItemSearch(bin.id, value);
                            }
                          }}
                          getOptionLabel={getItemLabel}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          filterOptions={(options) => options}
                          noOptionsText={
                            isExceptionSearchLoading ? 'Searching...' : 'Type to search items'
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search item"
                              placeholder="Type item name..."
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`exception-rule-label-${bin.id}`}>Rule</InputLabel>
                          <Select
                            labelId={`exception-rule-label-${bin.id}`}
                            label="Rule"
                            value={exceptionDraft.ruleType}
                            onChange={(event) =>
                              setExceptionDraftsByBin((current) => ({
                                ...current,
                                [bin.id]: {
                                  ...(current[bin.id] ?? emptyAddExceptionDraft),
                                  ruleType: event.target.value as 'include' | 'exclude',
                                },
                              }))
                            }
                          >
                            <MenuItem value="include">Include</MenuItem>
                            <MenuItem value="exclude">Exclude</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddException(bin.id)}
                          disabled={exceptionDraft.isAdding}
                        >
                          {exceptionDraft.isAdding ? 'Adding...' : 'Add'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <Dialog open={isCreateBinDialogOpen} onClose={() => setCreateBinDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add bin</DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label="Title"
              value={newBinTitle}
              onChange={(event) => setNewBinTitle(event.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Color"
              value={newBinColor}
              onChange={(event) => setNewBinColor(event.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Contrast color"
              value={newBinContrastColor}
              onChange={(event) => setNewBinContrastColor(event.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBinDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateBin} disabled={isCreatingBin}>
            {isCreatingBin ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast(null)} severity={toast?.severity ?? 'success'} sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BinGuidelines;
